import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ROLLING_PLACEHOLDERS = [
  'Thinking...',
  'Looking that up...',
  'Processing your request...',
  'Just a moment...',
  'Searching for the best answer...',
  'Analysing...',
  'Preparing a response...',
];

const MOBILE_HIDE_TRIGGER_DELTA = 18;
const MOBILE_SHOW_TRIGGER_DELTA = 10;
const MOBILE_SCROLL_EDGE_GUARD = 4;
const MOBILE_TOGGLE_LOCK_MS = 220;
const MOBILE_BOTTOM_SHOW_GUARD_PX = 72;
const MOBILE_BOTTOM_SHOW_TRIGGER_DELTA = 36;

const AttachmentPreview = ({ att, onOpen }) => {
  // Derive effective type from att.type, mimeType, or filename extension
  // Use att.filename preferentially; if falling back to internal_fileID, strip the username/ prefix first
  const nameForExt = att.filename || (att.internal_fileID ? att.internal_fileID.split('/').pop() : '');
  const ext = nameForExt.split('.').pop().toLowerCase();
  const effectiveType = (() => {
    if (att.type === 'image' || (att.mimeType || att.mime_type || '').startsWith('image/')) return 'image';
    if (att.type === 'html' || (att.mimeType || att.mime_type) === 'text/html' || ext === 'html' || ext === 'htm') return 'html';
    if (att.type === 'md' || (att.mimeType || att.mime_type) === 'text/markdown' || ext === 'md' || ext === 'markdown') return 'md';
    return att.type || 'document';
  })();

  // Resolved signed URL — starts with att.url if already present
  const [resolvedUrl, setResolvedUrl] = useState(att.url || null);
  const [urlLoading, setUrlLoading] = useState(!att.url && !!att.internal_fileID);
  // For md/html types: fetched text content
  const [content, setContent] = useState(att.content || '');
  const [contentLoading, setContentLoading] = useState(false);

  // Step 1: resolve signed URL from internal_fileID when no url is provided
  useEffect(() => {
    if (att.url) { setResolvedUrl(att.url); return; }
    if (!att.internal_fileID) return;

    setUrlLoading(true);
    axios.get(`/api/files/url?internal_fileID=${encodeURIComponent(att.internal_fileID)}`)
      .then(res => setResolvedUrl(res.data.url))
      .catch(err => console.error('[AttachmentPreview] URL resolve failed:', att.internal_fileID, err.message))
      .finally(() => setUrlLoading(false));
  }, [att.internal_fileID, att.url]);

  // Step 2: for md/html, fetch text content via server proxy (avoids S3 CORS issues)
  useEffect(() => {
    if (content || (effectiveType !== 'md' && effectiveType !== 'html')) return;
    if (!att.internal_fileID) return;

    setContentLoading(true);
    axios.get(`/api/files/content?internal_fileID=${encodeURIComponent(att.internal_fileID)}`)
      .then(res => setContent(typeof res.data === 'string' ? res.data : JSON.stringify(res.data)))
      .catch(err => console.error('[AttachmentPreview] Content fetch failed:', err.message))
      .finally(() => setContentLoading(false));
  }, [att.internal_fileID, effectiveType, content]);

  // Loading spinner shown while resolving URL
  if (urlLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-400 w-fit animate-pulse">
        <span className="material-symbols-outlined text-[18px]">hourglass_top</span>
        <span className="truncate max-w-[200px]">{att.filename || 'Loading...'}</span>
      </div>
    );
  }

  if (effectiveType === 'image') {
    if (resolvedUrl) {
      return (
        <img
          src={resolvedUrl}
          alt={att.filename || 'Image'}
              onClick={onOpen ? () => onOpen({ type: 'image', url: resolvedUrl, filename: att.filename || 'Image' }) : undefined}
          className={`max-w-xs max-h-64 rounded-lg object-cover border border-gray-200 shadow-sm ${onOpen ? 'cursor-pointer hover:opacity-90 hover:ring-2 hover:ring-primary transition-all' : ''}`}
          title={onOpen ? 'Click to open in Canvas' : undefined}
        />
      );
    }
    // URL could not be resolved — show filename chip
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 w-fit">
        <span className="material-symbols-outlined text-[18px] text-primary">image</span>
        <span className="truncate max-w-[200px]">{att.filename || att.internal_fileID || 'Image'}</span>
      </div>
    );
  }

  if (effectiveType === 'html') {
    return (
      <div className="rounded-lg overflow-hidden border border-gray-200 bg-white flex flex-col w-full max-w-[400px]">
        <div className="bg-gray-100 px-3 py-1.5 text-xs text-gray-500 border-b border-gray-200 font-medium flex items-center gap-2 shrink-0">
          <span className="material-symbols-outlined text-[14px]">code</span>
          <span className="truncate">{att.filename || 'HTML Preview'}</span>
          {!onOpen && resolvedUrl && (
            <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="ml-auto hover:text-primary transition-colors flex items-center" title="Open in new tab">
              <span className="material-symbols-outlined text-[14px]">open_in_new</span>
            </a>
          )}
        </div>
        {/* Iframe wrapper — click overlay opens in Canvas when onOpen is provided */}
        <div className="relative">
          {contentLoading ? (
            <div className="p-4 text-center text-gray-400 text-sm">Loading preview...</div>
          ) : content ? (
            <iframe srcDoc={content} className="w-full h-[300px] bg-white border-0 pointer-events-none" sandbox="allow-scripts allow-same-origin" title={att.filename || 'HTML Preview'} />
          ) : resolvedUrl ? (
            <iframe src={resolvedUrl} className="w-full h-[300px] bg-white border-0 pointer-events-none" sandbox="allow-scripts allow-same-origin" title={att.filename || 'HTML Preview'} />
          ) : (
            <div className="p-4 text-center text-gray-400 text-sm">Preview unavailable</div>
          )}
          {onOpen && (resolvedUrl || content) && (
            <div
              onClick={() => onOpen({ type: effectiveType, url: resolvedUrl, content, filename: att.filename || 'HTML Preview' })}
              className="absolute inset-0 cursor-pointer flex items-center justify-center bg-transparent hover:bg-primary/5 transition-colors group"
              title="Click to open in Canvas"
            >
              <span className="material-symbols-outlined text-[32px] text-primary opacity-0 group-hover:opacity-80 transition-opacity drop-shadow">open_in_browser</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (effectiveType === 'md') {
    return (
      <div className="rounded-lg overflow-hidden border border-gray-200 bg-white flex flex-col w-full max-w-[400px]">
        <div className="bg-gray-100 px-3 py-1.5 text-xs text-gray-500 border-b border-gray-200 font-medium flex items-center gap-2 shrink-0">
          <span className="material-symbols-outlined text-[14px]">markdown</span>
          <span className="truncate">{att.filename || 'Markdown Preview'}</span>
          {!onOpen && resolvedUrl && (
            <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="ml-auto hover:text-primary transition-colors flex items-center" title="Open in new tab">
              <span className="material-symbols-outlined text-[14px]">open_in_new</span>
            </a>
          )}
        </div>
        <div
          onClick={onOpen && (resolvedUrl || content) ? () => onOpen({ type: effectiveType, url: resolvedUrl, content, filename: att.filename || 'Markdown Preview' }) : undefined}
          className={`p-4 max-h-[300px] overflow-y-auto markdown-preview text-[14px] bg-white ${onOpen && (resolvedUrl || content) ? 'cursor-pointer hover:bg-primary/5 transition-colors' : ''}`}
          title={onOpen && (resolvedUrl || content) ? 'Click to open in Canvas' : undefined}
        >
          {contentLoading ? (
            <div className="text-center text-gray-400 text-sm">Loading preview...</div>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || '*No content available*'}</ReactMarkdown>
          )}
        </div>
      </div>
    );
  }

  // Fallback for document or unknown types
  return (
    <a
      href={resolvedUrl || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-primary hover:bg-primary/5 transition-colors w-fit"
    >
      <span className="material-symbols-outlined text-[18px]">attach_file</span>
      <span className="truncate max-w-[200px]">{att.filename || att.internal_fileID || 'Attachment'}</span>
    </a>
  );
};

function ChatInterface({ mode, config, language, setLanguage, user, onScrollDirectionChange, mobileUiHidden }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [training, setTraining] = useState(false);
  // attachments: Array<{ file: File, previewUrl: string|null, name: string, mimeType: string }>
  const [attachments, setAttachments] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [draft, setDraft] = useState('');
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(500);
  const [isResizing, setIsResizing] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  // canvasContent: { type: 'image'|'html'|'md', url: string, filename: string } | null
  const [canvasContent, setCanvasContent] = useState(null);
  const [rollingPlaceholderIndex, setRollingPlaceholderIndex] = useState(0);
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const quickReplyUploadLabelRef = useRef(null); // stores label of upload quick-reply pending file pick
  const lastScrollY = useRef(0);
  const scrollAccumulatedDelta = useRef(0);
  const scrollToggleLockUntil = useRef(0);
  
  // Voice input/output state
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [voiceInputUsed, setVoiceInputUsed] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);

  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  };

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const handleAttachmentSelect = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const newAttachments = Array.from(files).map(file => {
      const isImage = file.type.startsWith('image/');
      const previewUrl = isImage ? URL.createObjectURL(file) : null;
      return { file, previewUrl, name: file.name, mimeType: file.type };
    });
    setAttachments(prev => [...prev, ...newAttachments]);
    // Reset input so same file can be re-selected
    e.target.value = '';

    // If this was triggered by an upload quick-reply, auto-send with the stored label
    if (quickReplyUploadLabelRef.current) {
      const label = quickReplyUploadLabelRef.current;
      quickReplyUploadLabelRef.current = null;
      // Wait a tick for attachments state to update, then send with the quick-reply label
      setTimeout(() => sendMessage(null, label), 50);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearAllAttachments = () => {
    attachments.forEach(att => {
      if (att.previewUrl) URL.revokeObjectURL(att.previewUrl);
    });
    setAttachments([]);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 150)}px`;
    }
  }, [input]);

  // Cycle text in the bot bubble while waiting for a reply (#46)
  useEffect(() => {
    if (!loading) {
      setRollingPlaceholderIndex(0);
      return;
    }
    setRollingPlaceholderIndex(0);
    const interval = setInterval(() => {
      setRollingPlaceholderIndex(i => (i + 1) % ROLLING_PLACEHOLDERS.length);
    }, 1800);
    return () => clearInterval(interval);
  }, [loading]);

  // Report scroll direction to parent so it can auto-hide mobile header/nav (#43)
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el || !onScrollDirectionChange) return;

    const handleScroll = () => {
      const now = performance.now();
      const maxScroll = el.scrollHeight - el.clientHeight;
      const currentY = Math.max(0, Math.min(el.scrollTop, Math.max(maxScroll, 0)));
      const diff = currentY - lastScrollY.current;

      if (maxScroll <= 0) {
        onScrollDirectionChange('up');
        lastScrollY.current = currentY;
        scrollAccumulatedDelta.current = 0;
        return;
      }

      const atTop = currentY <= MOBILE_SCROLL_EDGE_GUARD;
      const atBottom = currentY >= maxScroll - MOBILE_SCROLL_EDGE_GUARD;
      const nearBottom = currentY >= maxScroll - MOBILE_BOTTOM_SHOW_GUARD_PX;

      if (now < scrollToggleLockUntil.current) {
        lastScrollY.current = currentY;
        return;
      }

      if (atTop) {
        onScrollDirectionChange('up');
        lastScrollY.current = currentY;
        scrollAccumulatedDelta.current = 0;
        return;
      }

      // When UI is hidden and user is near bottom, require a stronger upward pull
      // before showing again (prevents momentum bounce from snapping it back).
      if (mobileUiHidden && nearBottom && diff < 0) {
        scrollAccumulatedDelta.current += diff;
        lastScrollY.current = currentY;
        if (scrollAccumulatedDelta.current > -MOBILE_BOTTOM_SHOW_TRIGGER_DELTA) {
          return;
        }
      }

      // Ignore tiny deltas and bottom-edge bounce noise.
      if (Math.abs(diff) < 1 || (atBottom && diff > 0)) {
        lastScrollY.current = currentY;
        return;
      }

      if (Math.sign(scrollAccumulatedDelta.current) !== Math.sign(diff)) {
        scrollAccumulatedDelta.current = 0;
      }

      scrollAccumulatedDelta.current += diff;
      lastScrollY.current = currentY;

      if (scrollAccumulatedDelta.current >= MOBILE_HIDE_TRIGGER_DELTA) {
        onScrollDirectionChange('down');
        scrollAccumulatedDelta.current = 0;
        scrollToggleLockUntil.current = now + MOBILE_TOGGLE_LOCK_MS;
      } else if (scrollAccumulatedDelta.current <= -(mobileUiHidden && nearBottom ? MOBILE_BOTTOM_SHOW_TRIGGER_DELTA : MOBILE_SHOW_TRIGGER_DELTA)) {
        onScrollDirectionChange('up');
        scrollAccumulatedDelta.current = 0;
        scrollToggleLockUntil.current = now + MOBILE_TOGGLE_LOCK_MS;
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [mobileUiHidden, onScrollDirectionChange]);

  // Resizing logic
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = document.body.clientWidth - e.clientX;
      if (newWidth > 300 && newWidth < 800) {
        setCanvasWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        document.body.style.cursor = 'default';
        document.body.style.userSelect = 'auto';
      }
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Check browser support for Web Speech API
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const hasSpeech = !!SpeechRecognition && !!window.speechSynthesis;
    
    // Explicitly check for Firefox and disable even if API exists
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    
    setSpeechSupported(hasSpeech && !isFirefox);
    
    if (isFirefox) {
      console.log('[Voice] Firefox detected - voice features disabled');
    } else if (hasSpeech) {
      console.log('[Voice] Web Speech API supported');
    } else {
      console.warn('[Voice] Web Speech API not supported');
    }
  }, []);

  // Pre-load speech synthesis voices
  useEffect(() => {
    if (window.speechSynthesis) {
      // Voices load asynchronously in some browsers
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        const voices = window.speechSynthesis.getVoices();
        console.log('[Voice] Available voices:', voices.length);
      };
    }
  }, []);

  // Update speech recognition language when app language changes
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = language === 'zh-TW' ? 'zh-TW' : 'en-US';
    }
  }, [language]);

  // Stop recording when tab hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isRecording) {
        // Stop recognition directly
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Track desktop breakpoint for canvas panel layout
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Load chat history when user or mode changes
  useEffect(() => {
    const fetchHistory = async () => {
      if (!user?.email) {
        console.log('[History] No user email, skipping');
        return;
      }

      console.log('[History] Fetching for:', user.email, 'mode:', mode);

      try {
        setLoading(true);
        const response = await axios.get(`/api/chat/history?email=${encodeURIComponent(user.email)}`);
        const historyData = response.data;

        console.log('[History] Received data, is array:', Array.isArray(historyData), 'length:', historyData?.length);

        if (Array.isArray(historyData)) {
          const parsedMessages = historyData.map(item => {
            let content = '';
            
            if (item.from === 'human') {
              try {
                const payloadObj = JSON.parse(item.payload);
                content = payloadObj.message || item.payload;
              } catch (e) {
                content = item.payload;
              }
            } else {
              // Bot payload may be raw JSON from n8n — try to extract text
              try {
                const payloadObj = JSON.parse(item.payload);
                content = payloadObj.text || payloadObj.output || payloadObj.reply || payloadObj.message || item.payload;
              } catch (e) {
                content = item.payload;
              }
            }

            const timestamp = new Date(item.createdAt);

            return {
              type: item.from === 'human' ? 'user' : 'bot',
              content: content,
              timestamp: timestamp
            };
          });

          parsedMessages.sort((a, b) => a.timestamp - b.timestamp);
          
          console.log('[History] Parsed', parsedMessages.length, 'messages, calling setMessages');
          setMessages(parsedMessages);
          console.log('[History] setMessages called');
          
          setTimeout(() => scrollToBottom('auto'), 0);
          
          const lastUserMsg = [...historyData].reverse().find(m => m.from === 'human');
          if (lastUserMsg) {
             try {
                const payloadObj = JSON.parse(lastUserMsg.payload);
                if (payloadObj.sessionId) {
                  setSessionId(payloadObj.sessionId);
                }
             } catch (e) {
               // ignore
             }
          }
        } else {
          console.log('[History] Response is not an array:', historyData);
        }
      } catch (error) {
        console.error('[History] Failed to load chat history:', error);
      } finally {
        console.log('[History] Setting loading to false');
        setLoading(false);
      }
    };

    fetchHistory();
  }, [user, mode]);

  // Initialize speech recognition
  const initSpeechRecognition = () => {
    if (recognitionRef.current) return recognitionRef.current;
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language === 'zh-TW' ? 'zh-TW' : 'en-US';
    
    recognition.onstart = () => {
      console.log('[Voice] Recording started');
      setIsRecording(true);
    };
    
    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      
      setTranscript(finalTranscript || interimTranscript);
    };
    
    recognition.onerror = (event) => {
      console.error('[Voice] Recognition error:', event.error);
      setIsRecording(false);
      if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please enable microphone permissions.');
      }
    };
    
    recognition.onend = () => {
      console.log('[Voice] Recording ended');
      setIsRecording(false);
    };
    
    recognitionRef.current = recognition;
    return recognition;
  };

  // Start voice recording
  const startVoiceRecording = () => {
    if (!speechSupported) return;
    
    // Cancel any ongoing TTS when user starts recording
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    
    setTranscript('');
    setVoiceInputUsed(true);
    const recognition = initSpeechRecognition();
    recognition.start();
  };

  // Stop voice recording and send message
  const stopVoiceRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    
    setTimeout(() => {
      const finalTranscript = transcript.trim();
      setTranscript('');
      
      if (finalTranscript) {
        // Send the message directly with the transcript text
        sendMessage(null, finalTranscript);
      }
    }, 300);
  };

  // Text-to-speech with sentence chunking
  const speakText = (text) => {
    if (!window.speechSynthesis || !text) return;
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    // Strip markdown formatting
    const cleanText = text
      .replace(/[#*_`~[\]]/g, '')
      .replace(/\n+/g, '. ')
      .trim();
    
    if (!cleanText) return;
    
    // Split into sentences to avoid browser limits (Chrome ~15s per utterance)
    const sentences = cleanText.match(/[^.!?]+[.!?]+/g) || [cleanText];
    
    let currentIndex = 0;
    
    const speakNextSentence = () => {
      if (currentIndex >= sentences.length) return;
      
      const utterance = new SpeechSynthesisUtterance(sentences[currentIndex].trim());
      utterance.lang = language === 'zh-TW' ? 'zh-HK' : 'en-US';
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        language === 'zh-TW' 
          ? (voice.lang === 'zh-HK' || voice.lang.startsWith('zh-HK'))
          : voice.lang.startsWith('en')
      );
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      utterance.onend = () => {
        currentIndex++;
        speakNextSentence(); // Continue to next sentence
      };
      
      utterance.onerror = (event) => {
        console.error('[Voice] TTS error:', event.error);
      };
      
      window.speechSynthesis.speak(utterance);
    };
    
    speakNextSentence();
  };

  const sendMessage = async (e, overrideText = null) => {
    if (e) e.preventDefault();
    
    // Cancel any ongoing TTS when user starts a new message
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    
    const wasVoiceInput = voiceInputUsed;
    
    // Use overrideText if provided, otherwise use input state
    const messageText = overrideText !== null ? overrideText : input;
    
    if ((!messageText.trim() && attachments.length === 0) || loading) return;

    let currentSessionId = sessionId;
    if (!currentSessionId) {
      currentSessionId = generateSessionId();
      setSessionId(currentSessionId);
    }

    const userMessage = {
      type: 'user',
      content: messageText,
      timestamp: new Date(),
      attachments: attachments.length > 0
        ? attachments.map(att => ({ name: att.name, mimeType: att.mimeType, previewUrl: att.previewUrl }))
        : []
    };

    setMessages(prev => [...prev, userMessage]);
    const sentInput = messageText;
    const sentAttachments = [...attachments];
    setHistory(prev => {
      const newHistory = [...prev, messageText];
      return newHistory.length > 50 ? newHistory.slice(newHistory.length - 50) : newHistory;
    });
    setHistoryIndex(-1);
    setDraft('');
    setInput('');
    setAttachments([]);
    setLoading(true);

    try {
      const endpoint = mode === 'basic' ? '/api/chat/basic' : '/api/chat/knowledge';

      // Step 1: if there are attachments, upload them to get internal_fileIDs
      let uploadedAttachments = [];
      if (sentAttachments.length === 1) {
        // Single file — use the original single-file endpoint
        const att = sentAttachments[0];
        console.log(`[Upload] Uploading 1 attachment: ${att.name}`);
        const formData = new FormData();
        formData.append('attachment', att.file, att.name);
        if (user?.email) formData.append('email', user.email);
        const uploadRes = await axios.post('/api/files/upload-chat-attachment', formData);
        console.log('[Upload] Success:', uploadRes.data);
        uploadedAttachments = [uploadRes.data];
      } else if (sentAttachments.length > 1) {
        // Multiple files — use batch endpoint (server handles sequential n8n calls)
        console.log(`[Upload] Batch uploading ${sentAttachments.length} attachments`);
        const formData = new FormData();
        sentAttachments.forEach(att => {
          formData.append('attachments', att.file, att.name);
        });
        if (user?.email) formData.append('email', user.email);
        const uploadRes = await axios.post('/api/files/upload-chat-attachments', formData);
        console.log('[Upload] Batch success:', uploadRes.data);
        // Filter out any files that failed to upload (they'll have an error field)
        const allResults = uploadRes.data;
        const failed = allResults.filter(a => a.error);
        if (failed.length > 0) {
          console.warn('[Upload] Some files failed:', failed.map(f => `${f.filename}: ${f.error}`));
        }
        uploadedAttachments = allResults.filter(a => !a.error);
      }

      // If no message text, generate a default from the uploaded file info
      const effectiveMessage = sentInput.trim() ||
        (uploadedAttachments.length > 0
          ? uploadedAttachments.map(a => `File: ${a.filename || a.id}`).join(', ')
          : sentAttachments.length > 0
          ? sentAttachments.map(a => `File: ${a.name}`).join(', ')
          : '');

      // Step 2: send chat message with attachment metadata (no binary)
      // Send as `attachments` array; include `attachment` (first item) for backward compatibility
      const response = await axios.post(endpoint, {
        message: effectiveMessage,
        sessionId: currentSessionId,
        language: language,
        email: user?.email,
        training,
        attachment: uploadedAttachments.length > 0 ? uploadedAttachments[0] : null,
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined
      }, { timeout: 30000 });

      let replyContent = '';
      if (typeof response.data === 'string') {
        replyContent = response.data;
      } else if (typeof response.data.text === 'string') {
        replyContent = response.data.text;
      } else if (typeof response.data.output === 'string') {
        replyContent = response.data.output;
      } else if (typeof response.data.reply === 'string') {
        replyContent = response.data.reply;
      } else if (typeof response.data.message === 'string') {
        replyContent = response.data.message;
      } else {
        replyContent = JSON.stringify(response.data);
      }

      const botMessage = {
        type: 'bot',
        content: replyContent,
        timestamp: new Date(response.data.timestamp || Date.now()),
        attachments: response.data.attachments || [],
        buttons: response.data.buttons || [],
        button_layout: response.data.button_layout || [],
        menu: response.data.menu || null
      };

      setMessages(prev => [...prev, botMessage]);
      
      // Auto-play TTS if this message used voice input
      if (wasVoiceInput && speechSupported) {
        setTimeout(() => speakText(botMessage.content), 500);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const isTimeout = error.code === 'ECONNABORTED' || error.response?.status === 504;
      const errorDetails = isTimeout
        ? 'The response is taking longer than usual. Please try again.'
        : error.response?.data?.error || error.message || 'Unknown error';
      const errorMessage = {
        type: 'error',
        content: `Sorry, I encountered an error: ${errorDetails}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      setVoiceInputUsed(false); // Reset voice input flag
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const clearChatHistory = async () => {
    if (!user?.email) {
      console.error('[Clear Chat] No user email, skipping');
      return;
    }

    try {
      setLoading(true);
      setClearing(true);
      console.log('[Clear Chat] Sending clear history request for:', user.email);
      const response = await axios.delete(`/api/chat/clear-history?email=${encodeURIComponent(user.email)}`);

      if (response.status === 200) {
        console.log('[Clear Chat] History cleared successfully');
        setMessages([]);
        setSessionId(null);
      } else {
        console.error('[Clear Chat] Failed to clear history, status:', response.status);
      }
    } catch (error) {
      console.error('[Clear Chat] Error clearing history:', error);
    } finally {
      setLoading(false);
      setClearing(false);
    }
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div className="flex h-full min-h-0 bg-white overflow-hidden">
      {/* Main Chat Content */}
      <section className="flex-1 flex flex-col min-w-0 min-h-0 bg-white md:border-r border-gray-100 relative">
        {/* Voice Recording Overlay */}
        {isRecording && (
          <div className="absolute inset-0 z-50 bg-black/50 flex items-center justify-center pointer-events-none">
            <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-sm mx-4">
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                  <span className="material-symbols-outlined text-white text-[32px]">mic</span>
                </div>
                <p className="text-lg font-medium text-gray-800">
                  {language === 'zh-TW' ? '正在聆聽...' : 'Listening...'}
                </p>
                <p className="text-sm text-gray-500 text-center min-h-[60px] max-w-xs">
                  {transcript || (language === 'zh-TW' ? '放開以發送' : 'Release to send')}
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <header className="h-16 shrink-0 border-b border-gray-100 flex items-center justify-between px-6 bg-white z-20">
          <h2 className="text-gray-800 font-bold text-lg tracking-tight">
            {mode === 'basic' ? 'Chat' : 'Knowledge Chat'}
          </h2>
          <div className="flex items-center gap-3">
            {config?.trainingMode && (
              <label className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50">
                <span className="text-[11px] font-semibold text-gray-700">
                  {language === 'zh-TW' ? '訓練模式' : 'Training'}
                </span>
                <button
                  type="button"
                  onClick={() => setTraining(prev => !prev)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    training ? 'bg-primary' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      training ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </label>
            )}
            {/* Canvas button - desktop header, always visible */}
            <button
              onClick={() => setCanvasOpen(prev => !prev)}
              className="hidden lg:flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full transition-all border border-gray-200 text-gray-500 hover:text-primary hover:bg-blue-50 hover:border-primary/30"
              title="Toggle Canvas"
            >
              <span className="material-symbols-outlined text-[18px]">article</span>
              <span>Canvas</span>
            </button>
            <button
              onClick={clearChatHistory}
              disabled={loading || clearing}
              className="hidden lg:flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-full transition-all border border-gray-200"
              title={language === 'zh-TW' ? '重新開始' : 'Restart Chat'}
            >
              <span className={`material-symbols-outlined text-[18px] ${clearing ? 'animate-spin' : ''}`}>
                {clearing ? 'refresh' : 'chat_add_on'}
              </span>
              <span>
                {clearing
                  ? (language === 'zh-TW' ? '重置中...' : 'Restarting...')
                  : (language === 'zh-TW' ? '新對話' : 'New Chat')}
              </span>
            </button>
            <div className="flex items-center gap-2 bg-gray-50 pl-2 pr-3 py-1.5 rounded-full border border-gray-200 shadow-sm">
              <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]"></div>
              <span className="text-[10px] font-bold text-gray-500 tracking-wider uppercase">
                {language === 'zh-TW' ? 'AI 助理' : 'AI Assistant'}
              </span>
            </div>
          </div>
        </header>

        {/* Mobile toolbar - hidden when scrolling down */}
        <div
          className={`lg:hidden flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-white/95 backdrop-blur-sm ${mobileUiHidden ? 'hidden' : ''}`}
        >
          {config?.trainingMode && (
            <label className="flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-gray-200 bg-gray-50 shrink-0">
              <span className="text-[11px] font-semibold text-gray-700">
                {language === 'zh-TW' ? '訓練' : 'Training'}
              </span>
              <button
                type="button"
                onClick={() => setTraining(prev => !prev)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  training ? 'bg-primary' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    training ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </label>
          )}
          <div className="flex-1" />
          {/* Canvas button - always visible on mobile toolbar */}
          <button
            onClick={() => setCanvasOpen(prev => !prev)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-all border shrink-0 text-primary bg-blue-50 border-primary/20 hover:bg-blue-100"
          >
            <span className="material-symbols-outlined text-[16px]">article</span>
            <span>Canvas</span>
          </button>
          <button
            onClick={clearChatHistory}
            disabled={loading || clearing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-red-500 bg-gray-50 hover:bg-red-50 rounded-full transition-all border border-gray-200 shrink-0"
          >
            <span className={`material-symbols-outlined text-[16px] ${clearing ? 'animate-spin' : ''}`}>
              {clearing ? 'refresh' : 'chat_add_on'}
            </span>
            <span>{language === 'zh-TW' ? '新對話' : 'New Chat'}</span>
          </button>
        </div>

        {/* Chat Messages */}
        <div ref={messagesScrollRef} className="flex-1 overflow-y-auto hide-scrollbar p-4 md:p-6 space-y-6 bg-white">
          <div className="flex justify-center py-2">
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest bg-gray-50 px-4 py-1.5 rounded-full border border-gray-100">
              {new Date().toLocaleDateString(language === 'zh-TW' ? 'zh-TW' : 'en-US', { 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
          {config?.trainingMode && (
            <div className="flex justify-center lg:hidden">
              <span
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${
                  training
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-gray-100 text-gray-600 border-gray-200'
                }`}
              >
                <span className="material-symbols-outlined text-[14px]">school</span>
                {language === 'zh-TW'
                  ? `訓練模式：${training ? '開啟' : '關閉'}`
                  : `Training: ${training ? 'ON' : 'OFF'}`}
              </span>
            </div>
          )}
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <span className="material-symbols-outlined text-[48px] text-primary">
                  {mode === 'basic' ? 'chat_bubble' : 'psychology'}
                </span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{config?.chatWelcomeTitle || 'Start a Conversation'}</h3>
              <p className="text-gray-500 text-center max-w-md">
                {mode === 'basic'
                  ? (config?.chatWelcomeSubtitle || 'Type your message below to begin chatting with the AI assistant.')
                  : (config?.chatKnowledgeWelcomeSubtitle || 'Ask questions about your documents and I\'ll provide answers based on your knowledge base.')}
              </p>
            </div>
          ) : (
            messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.type === 'user' ? 'justify-end items-start' : 'justify-start items-start'} ${
                  msg.type === 'system' ? 'justify-center' : ''
                } group`}
              >
                {msg.type === 'system' ? (
                  <div className="px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-600 font-medium">
                    {msg.content}
                  </div>
                ) : (
                  <>
                    {msg.type !== 'user' && (
                      <div className="w-8 h-8 shrink-0 rounded-full bg-bubble-ai border border-blue-50 flex items-center justify-center shadow-sm mr-3">
                        <span className="material-symbols-outlined text-primary text-[18px]">
                          {msg.type === 'error' ? 'error' : 'smart_toy'}
                        </span>
                      </div>
                    )}
                    <div className={`flex flex-col gap-1.5 max-w-[85%] ${msg.type === 'user' ? 'items-end' : 'items-start'}`}>
                      <div className={`px-4 py-3 rounded-input text-[14px] leading-relaxed shadow-sm ${
                        msg.type === 'user'
                          ? 'bg-primary text-white rounded-tr-none'
                          : msg.type === 'error'
                          ? 'bg-red-50 text-red-800 border border-red-200 rounded-tl-none'
                          : 'bg-bubble-ai border border-blue-50 text-slate-700 rounded-tl-none'
                      }`}>
                        {msg.type === 'user' || msg.type === 'error' ? (
                          <>
                            {msg.type === 'user' && msg.attachments && msg.attachments.length > 0 && (
                              <div className="mb-2 flex flex-col gap-2">
                                {msg.attachments.map((att, attIdx) => (
                                  <div key={attIdx}>
                                    {att.previewUrl ? (
                                      <img
                                        src={att.previewUrl}
                                        alt={att.name}
                                        onClick={() => {
                                          setCanvasContent({ type: 'image', url: att.previewUrl, filename: att.name });
                                          setCanvasOpen(true);
                                        }}
                                        className="max-w-[200px] max-h-[200px] rounded-lg object-cover border border-white/20 cursor-pointer hover:opacity-90 hover:ring-2 hover:ring-white/60 transition-all"
                                        title="Click to open in Canvas"
                                      />
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          // For non-image user attachments, try to open in canvas by type
                                          const ext = att.name?.split('.').pop()?.toLowerCase();
                                          const type = ext === 'html' || ext === 'htm' ? 'html' : ext === 'md' ? 'md' : null;
                                          if (type && att.previewUrl) {
                                            setCanvasContent({ type, url: att.previewUrl, filename: att.name });
                                            setCanvasOpen(true);
                                          }
                                        }}
                                        className="flex items-center gap-2 px-3 py-2 bg-white/20 rounded-lg text-sm hover:bg-white/30 transition-colors"
                                      >
                                        <span className="material-symbols-outlined text-[18px]">attach_file</span>
                                        <span className="truncate max-w-[160px]">{att.name}</span>
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                          </>
                        ) : (
                          <div className="markdown-content">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)}</ReactMarkdown>
                            {msg.attachments && msg.attachments.length > 0 && (
                              <div className="mt-2 flex flex-col gap-2">
                                {msg.attachments.map((att, i) => (
                                  <div key={i}>
                                    <AttachmentPreview
                                      att={att}
                                      onOpen={(item) => {
                                        setCanvasContent(item);
                                        setCanvasOpen(true);
                                      }}
                                    />
                                    {(att.caption || att.caption_markdown) && (
                                      <p className="text-xs text-gray-500 mt-1">{att.caption || att.caption_markdown}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Quick reply buttons */}
                    {msg.type === 'bot' && msg.buttons && msg.buttons.length > 0 && (
                      <div className={`flex flex-wrap gap-2 mt-1 ${msg.button_layout === 'vertical' ? 'flex-col' : ''}`}>
                        {msg.buttons.map((btn, bIdx) => {
                          const label = btn.label || btn.text || btn.title || String(btn);
                          const isUpload = btn.type === 'upload' || btn.action === 'upload' || /upload|attach|file/i.test(label);
                          return (
                            <button
                              key={bIdx}
                              type="button"
                              onClick={() => {
                                if (isUpload) {
                                  quickReplyUploadLabelRef.current = label;
                                  fileInputRef.current?.click();
                                } else {
                                  sendMessage(null, label);
                                }
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary bg-white border border-primary/30 rounded-full hover:bg-primary/5 active:bg-primary/10 transition-colors shadow-sm"
                            >
                              {isUpload && <span className="material-symbols-outlined text-[14px]">attach_file</span>}
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {msg.type === 'user' && (
                      <div className="w-8 h-8 shrink-0 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 text-[10px] font-bold shadow-sm ml-3">
                        {user?.email ? user.email.substring(0, 2).toUpperCase() : 'ME'}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          )}
          {loading && (
            <div className="flex justify-start items-start group">
              <div className="w-8 h-8 shrink-0 rounded-full bg-bubble-ai border border-blue-50 flex items-center justify-center shadow-sm mr-3">
                <span className="material-symbols-outlined text-primary text-[18px]">smart_toy</span>
              </div>
              <div className="px-4 py-3 rounded-input bg-bubble-ai border border-blue-50 shadow-sm rounded-tl-none">
                <span className="text-sm text-gray-500 italic">{ROLLING_PLACEHOLDERS[rollingPlaceholderIndex]}</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Chat Input */}
        <div className="shrink-0 bg-white border-t border-gray-100 p-4 md:p-6 pb-[calc(1rem+env(safe-area-inset-bottom))] md:pb-6">
          <div className="max-w-3xl mx-auto">
            {/* Attachment preview chips */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {attachments.map((att, index) => (
                  <div key={index} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 border border-gray-200 rounded-full text-sm text-gray-700 max-w-xs">
                    {att.previewUrl ? (
                      <img src={att.previewUrl} alt="" className="w-5 h-5 rounded object-cover shrink-0" />
                    ) : (
                      <span className="material-symbols-outlined text-[16px] shrink-0 text-gray-500">attach_file</span>
                    )}
                    <span className="truncate">{att.name}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="ml-1 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                      aria-label="Remove attachment"
                    >
                      <span className="material-symbols-outlined text-[16px]">close</span>
                    </button>
                  </div>
                ))}
                {attachments.length > 1 && (
                  <button
                    type="button"
                    onClick={clearAllAttachments}
                    className="text-xs text-gray-400 hover:text-red-500 transition-colors px-2 py-1"
                    aria-label="Remove all attachments"
                  >
                    Clear all
                  </button>
                )}
              </div>
            )}
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleAttachmentSelect}
              accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.html,.htm,.md"
              multiple
            />
            <form onSubmit={sendMessage} className="relative group">
              <div className="relative flex items-end gap-2 bg-background-input rounded-input px-3 py-2 border border-gray-200 focus-within:border-primary/50 focus-within:ring-0 focus-within:bg-white transition-all shadow-sm">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  className="p-1 text-gray-400 hover:text-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0 self-end mb-0.5"
                  title={language === 'zh-TW' ? '附加檔案' : 'Attach file'}
                >
                  <span className="material-symbols-outlined text-[20px]">attach_file</span>
                </button>
                <textarea
                  ref={inputRef}
                  value={input}
                  rows={1}
                  onChange={(e) => {
                    setInput(e.target.value);
                    if (historyIndex !== -1) {
                      setHistoryIndex(-1);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                      e.preventDefault();
                      sendMessage(e);
                    } else if (e.key === 'ArrowUp' && !/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
                      if (history.length > 0) {
                        e.preventDefault();
                        if (historyIndex === -1) {
                          setDraft(input);
                          setHistoryIndex(history.length - 1);
                          setInput(history[history.length - 1]);
                        } else if (historyIndex > 0) {
                          setHistoryIndex(historyIndex - 1);
                          setInput(history[historyIndex - 1]);
                        }
                      }
                    } else if (e.key === 'ArrowDown' && !/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
                      if (historyIndex !== -1) {
                        e.preventDefault();
                        if (historyIndex < history.length - 1) {
                          setHistoryIndex(historyIndex + 1);
                          setInput(history[historyIndex + 1]);
                        } else {
                          setHistoryIndex(-1);
                          setInput(draft);
                        }
                      }
                    }
                  }}
                  className="flex-1 bg-transparent border-none focus:ring-0 text-[14px] p-0 placeholder-gray-400 text-gray-800 font-medium resize-none overflow-hidden leading-relaxed"
                  style={{ minHeight: '24px', maxHeight: '150px' }}
                  placeholder={language === 'zh-TW' ? '輸入訊息...' : 'Ask follow-up questions...'}
                  disabled={loading}
                />
                 <button
                   id="chat-send-btn"
                   type="submit"
                   disabled={loading || (!input.trim() && attachments.length === 0)}
                  className="w-8 h-8 rounded-lg bg-primary text-white flex items-center justify-center hover:bg-primary-dark active:scale-95 transition-all shadow-md shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 self-end"
                >
                  <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                </button>
                {/* Voice input button (Web Speech API) */}
                {speechSupported && (
                  <button
                    type="button"
                    onMouseDown={startVoiceRecording}
                    onMouseUp={stopVoiceRecording}
                    onMouseLeave={stopVoiceRecording}
                    onTouchStart={startVoiceRecording}
                    onTouchEnd={stopVoiceRecording}
                    disabled={loading}
                    className={`shrink-0 flex items-center justify-center rounded-full transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed self-end ${
                      isRecording
                        ? 'w-12 h-12 bg-red-500 text-white shadow-lg shadow-red-300 animate-pulse'
                        : 'w-10 h-10 bg-primary text-white hover:bg-primary-dark shadow-blue-200'
                    }`}
                    title={language === 'zh-TW' ? '按住說話' : 'Hold to speak'}
                  >
                    <span className="material-symbols-outlined text-[20px]">mic</span>
                  </button>
                )}
              </div>
            </form>
            <div className="text-center mt-2 hidden lg:block">
              <span className="text-[10px] text-gray-400 font-medium">
                {language === 'zh-TW'
                  ? 'AI 生成的內容可能不準確，請自行查證重要資訊。'
                  : 'AI can make mistakes. Check important info.'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Draggable Resizer - Desktop Only */}
      {canvasOpen && (
        <div
          onMouseDown={handleResizeStart}
          className={`hidden lg:block shrink-0 z-20 w-1 cursor-col-resize transition-colors ${
            isResizing ? 'bg-primary' : 'bg-transparent hover:bg-primary'
          }`}
        />
      )}

      {/* Canvas Panel - mobile: full-screen overlay, desktop: resizable sidebar */}
      {canvasOpen && (
        <aside
          className="flex flex-col bg-white z-50 fixed inset-0 lg:static lg:inset-auto lg:z-10 lg:shrink-0 lg:shadow-[-10px_0_15px_-3px_rgba(0,0,0,0.02)]"
          style={isDesktop ? { width: `${canvasWidth}px` } : undefined}
        >
          <header className="h-16 shrink-0 border-b border-gray-100 flex items-center justify-between px-6 bg-white">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-primary text-[20px]">
                  {canvasContent?.type === 'image' ? 'image' : canvasContent?.type === 'html' ? 'code' : canvasContent?.type === 'md' ? 'markdown' : 'article'}
                </span>
              </div>
              <h2 className="text-gray-800 font-bold text-lg tracking-tight truncate">
                {canvasContent ? canvasContent.filename : 'Canvas'}
              </h2>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canvasContent && (
                <button
                  onClick={() => setCanvasContent(null)}
                  className="p-2 text-gray-400 hover:text-primary hover:bg-gray-100 rounded-input transition-colors"
                  title="Clear canvas"
                >
                  <span className="material-symbols-outlined text-[20px]">close_fullscreen</span>
                </button>
              )}
              <button className="hidden lg:flex p-2 text-gray-400 hover:text-primary hover:bg-gray-100 rounded-input transition-colors" title="Undo">
                <span className="material-symbols-outlined text-[20px]">undo</span>
              </button>
              <button className="hidden lg:flex p-2 text-gray-400 hover:text-primary hover:bg-gray-100 rounded-input transition-colors" title="Redo">
                <span className="material-symbols-outlined text-[20px]">redo</span>
              </button>
              <button className="hidden lg:flex items-center gap-1.5 px-3 py-2 text-gray-400 hover:text-primary hover:bg-gray-100 rounded-input transition-colors group" title="Save">
                <span className="material-symbols-outlined text-[20px]">save</span>
                <span className="text-xs font-bold uppercase tracking-wider">Save</span>
              </button>
              <button className="hidden lg:flex p-2 text-gray-400 hover:text-primary hover:bg-gray-100 rounded-input transition-colors" title="Copy">
                <span className="material-symbols-outlined text-[20px]">content_copy</span>
              </button>
              <div className="hidden lg:block w-px h-4 bg-gray-200 mx-1"></div>
              <button
                onClick={() => setCanvasOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-input transition-colors"
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>
          </header>
          <div className="flex-1 overflow-y-auto hide-scrollbar bg-slate-50">
            {canvasContent ? (
              <div className="min-h-full bg-white flex flex-col">
                {canvasContent.type === 'image' && (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <img
                      src={canvasContent.url}
                      alt={canvasContent.filename}
                      className="max-w-full max-h-full rounded-lg shadow-md object-contain"
                    />
                  </div>
                )}
                {canvasContent.type === 'html' && (
                  canvasContent.content ? (
                    <iframe
                      srcDoc={canvasContent.content}
                      className="w-full flex-1 border-0"
                      style={{ minHeight: 'calc(100vh - 4rem)' }}
                      sandbox="allow-scripts allow-same-origin"
                      title={canvasContent.filename}
                    />
                  ) : canvasContent.url ? (
                    <iframe
                      src={canvasContent.url}
                      className="w-full flex-1 border-0"
                      style={{ minHeight: 'calc(100vh - 4rem)' }}
                      sandbox="allow-scripts allow-same-origin"
                      title={canvasContent.filename}
                    />
                  ) : (
                    <div className="flex items-center justify-center flex-1 text-gray-400 p-8">Preview unavailable</div>
                  )
                )}
                {canvasContent.type === 'md' && (
                  <div className="px-8 py-10 max-w-2xl mx-auto w-full">
                    <div className="prose prose-sm prose-slate max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {canvasContent.content || '*No content available*'}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <article className="min-h-full bg-white shadow-sm flex flex-col">
                <div className="bg-white border-b border-gray-50 px-8 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Internal Analysis Document</span>
                  </div>
                </div>
                <header className="px-8 pt-12 pb-8 max-w-2xl mx-auto w-full">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-2 py-0.5 bg-blue-50 text-primary text-[10px] font-bold uppercase tracking-wider rounded">Strategy</span>
                    <span className="text-[10px] text-gray-400 font-medium">8 min read</span>
                  </div>
                  <h1 className="text-3xl font-extrabold text-gray-900 leading-tight mb-6">
                    The Power of Generative AI in Modern Content Creation
                  </h1>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-primary font-bold text-xs">AI</div>
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-gray-900 leading-none">Lumina Analysis Bot</span>
                      <span className="text-[10px] text-gray-500 mt-1">October 24, 2024</span>
                    </div>
                  </div>
                </header>
                <div className="px-8 pb-16 max-w-2xl mx-auto w-full">
                  <div className="prose prose-sm prose-slate max-w-none text-slate-600 space-y-4">
                    <p className="text-base leading-relaxed">
                      The landscape of digital creation is undergoing a seismic shift driven by the emergence of Generative AI. 
                      Large Language Models (LLMs) are no longer just experimental tools; they have become central to how we 
                      conceptualize, draft, and refine content across every industry, from marketing to software development.
                    </p>
                  </div>
                </div>
              </article>
            )}
          </div>
        </aside>
      )}

    </div>
  );
}

export default ChatInterface;
